/**
 * 設定ページ (編集者管理、admin のみ)
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business app_access/creative の editorEmails 管理画面。admin PortalRole のみアクセス可。
 * @graph-connects api [calls] editors.list / editors.set
 */
import { useToast } from '@cortex/web-design-system/components/ui/Toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { orpc } from '../api/orpc.js';
import { useAuth } from '../components/auth-context.js';
import {
  EmptyState,
  Field,
  GlassCard,
  INPUT_CLASS,
  PrimaryButton,
  SectionHeading,
} from '../components/ui.js';

/**
 * 設定ページ。
 * @returns ページ要素
 * @graph-connects api [calls] editors.list / editors.set
 */
export function SettingsPage(): JSX.Element {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const editorsQuery = useQuery(
    orpc.editors.list.queryOptions({ input: {}, enabled: auth.canManageEditors }),
  );
  const [draft, setDraft] = useState<string | null>(null);

  const setMutation = useMutation(
    orpc.editors.set.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.editors.key() });
        setDraft(null);
        showToast('success', '編集者リストを更新しました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );

  if (!auth.canManageEditors) {
    return (
      <div>
        <SectionHeading title="Settings" />
        <EmptyState message="このページは admin のみアクセスできます" />
      </div>
    );
  }

  const editorEmails = editorsQuery.data?.editorEmails ?? [];
  const value = draft ?? editorEmails.join('\n');

  return (
    <div>
      <SectionHeading title="Settings" />
      <GlassCard>
        <h3 className="mb-2 text-sm font-bold text-foreground">編集者 (editorEmails)</h3>
        <p className="mb-4 text-body text-subtle">
          Design Guide を編集できるメンバーのメールアドレス (1 行 1 件)。admin PortalRole
          保持者はこのリストに関係なく常に編集できます。デザイナー等の非 PDG
          メンバーはここに登録してください。
        </p>
        <Field label="editorEmails">
          <textarea
            className={`${INPUT_CLASS} h-40 resize-y font-mono text-xs`}
            value={value}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
        </Field>
        <div className="flex justify-end">
          <PrimaryButton
            disabled={setMutation.isPending || draft === null}
            onClick={() => {
              setMutation.mutate({
                editorEmails: value
                  .split('\n')
                  .map((line) => line.trim())
                  .filter((line) => line !== ''),
              });
            }}
          >
            保存
          </PrimaryButton>
        </div>
      </GlassCard>
    </div>
  );
}
